import { html, css, LitElement } from "lit"
import { customElement, property, state } from "lit/decorators.js"
import { connect, FeedAPIResponse, StreamClient, StreamFeed } from "getstream"
import { msg, localized, str } from "@lit/localize"

enum ObjectType {
  actor = "actor",
  object = "object",
  target = "target",
}

/**
 * Custom Element to show notifications to the user.
 */
@localized()
@customElement("notification-menu")
export class NotificationMenu extends LitElement {
  @property({ type: String, attribute: "user-id" }) userId: string
  @property({ type: String, attribute: "stream-token" }) streamToken: string
  @property({ type: String, attribute: "stream-app-id" }) streamAppId: string
  @property({ type: String, attribute: "stream-api-key" }) streamApiKey: string
  @property({ type: Number, attribute: "unread-count" }) unreadCount: number

  @state() _showDropdown: boolean = false
  @state() _feedItems: Array<any> = []
  @state() _feed: StreamFeed

  client: StreamClient

  static styles = css`
    :host {
      position: relative;
    }

    button.bell {
      height: 2rem;
      width: 2rem;
      border-radius: 10rem;
      padding: 0.25rem;
      border: 0;
      box-shadow: var(--shadow);
      font-size: 1rem;
      background-color: rgba(243, 244, 246, 1);
    }

    button:hover {
      background-color: rgba(229, 231, 235, 1);
    }

    div.dropdown {
      position: absolute;
      right: 0;
      background-color: white;
      border: 1px solid rgba(229, 231, 235, 1);
      box-shadow: var(--shadow);
      border-radius: 0.375rem;
      width: 30rem;
      transform: translateY(0.5rem);
      overflow: hidden;
    }

    @media (max-width: 540px) {
      button.bell {
        background-color: transparent;
        color: white;
      }

      div.dropdown {
        width: 100vw;
        position: fixed;
      }
    }

    div.notification-icon {
      position: relative;
      display: flex;
      align-items: center;
    }

    div.notification-icon > div.counter {
      background-color: red;
      border-radius: 5rem;
      color: white;
      position: absolute;
      right: -5px;
      top: -5px;
      width: 1rem;
      height: 1rem;
      font-size: 0.675rem;
      text-align: center;
      padding: 0;
    }

    .dropdown > h3 {
      padding: 0.5rem 1rem;
      margin: 0;
      font-weight: 500;
      font-size: 0.875rem;
      background-color: rgba(243, 244, 246, 1);
      color: var(--colorGray900);
    }

    .dropdown > ul {
      padding: 0;
      margin: 0;
      list-style: none;
    }

    .dropdown > ul > li {
      padding: 0.25rem 1rem;
      border-top: 1px solid white;
      font-size: 0.9rem;
      display: flex;
    }

    .dropdown > ul > li > a {
      color: black;
      text-decoration: none;
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .datetime {
      font-size: 0.75rem;
      color: darkgray;
    }

    .dropdown > ul > li:hover {
      background-color: rgba(243, 244, 246, 1);
    }

    .dropdown > p {
      margin: 0;
      padding: 0.25rem;
      font-size: 0.75rem;
      text-align: center;
    }
  `

  /**
   * Fetch notifications and subscribe to realtime updates from getstream
   */
  async connectedCallback() {
    super.connectedCallback()

    // Connect to getstream to acquire client
    this.client = connect(
      this.streamApiKey,
      this.streamToken,
      this.streamAppId
    )

    // Define callback function for realtime updates from getstream
    const realtimeCallback = async (data) => {
      this.unreadCount += data.new.length
      await this.fetchFeedItems()
    }

    // Get feed of user using this.userId
    this._feed = this.client.feed("notifications", this.userId)

    // Subscribe to feed
    await this._feed.subscribe(realtimeCallback)

    // Fetch last 5 elements from notification feed and assign items
    await this.fetchFeedItems()
  }

  async fetchFeedItems() {
    const feedResponse: FeedAPIResponse = await this._feed.get({
      limit: 10,
      enrich: true
    })
    this._feedItems = feedResponse.results
    this.unreadCount = feedResponse.unseen
  }

  render() {
    return html`
      <div class="notification-icon">
        <button class="bell" @click="${this.toggleDropdown}">
          <!-- Heroicon name: bell -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            ></path>
          </svg>
        </button>
        ${this.unreadCount > 0
          ? html`
            <div class="counter">${this.unreadCount}</div>`
          : undefined}
      </div>
      ${this._showDropdown ? this.renderDropdown() : undefined}
    `
  }

  /**
   * Render dropdown with notification list
   */
  private renderDropdown() {
    return html`
      <div class="dropdown">
        <h3>${msg("Notifications")}</h3>
        ${this._feedItems.length === 0
          ? html`<p>No Notifications yet</p>`
          : html`
            <ul>
              ${this._feedItems.map(this.renderFeedItem.bind(this))}
            </ul>`}
      </div>`
  }

  /**
   * Render feedItem into HTML <li> tag. For this we must render actor,
   * object and target into strings and pick the correct natural language phrase
   * @param feedItem
   */
  private renderFeedItem(feedItem) {
    // Get actor, object and target.
    const actor = this.getAttributeFromNotification(
      feedItem,
      ObjectType.actor,
      "name"
    )
    const object = this.getAttributeFromNotification(
      feedItem,
      ObjectType.object,
      "name"
    )
    const target = this.getAttributeFromNotification(
      feedItem,
      ObjectType.target,
      "name"
    )

    // Get phrase and url based on verb
    let phrase
    let url
    switch (feedItem.verb) {
      case "added_as_co_editor":
        phrase = msg(str`${actor} added you as a co-editor to ${target}.`)
        url = this.getAttributeFromNotification(
          feedItem,
          ObjectType.target,
          "url"
        )
        break
      case "notify_platform":
        phrase = feedItem.activities[0]?.full_text
        url = feedItem.activities[0]?.url
        break
      case "published_script":
        phrase = msg(str`${actor} published a new script titled ${object}.`)
        url = this.getAttributeFromNotification(
          feedItem,
          ObjectType.object,
          "url"
        )
        break
      case "shared_thread":
        phrase = msg(str`${actor} shared a thread in ${target}`)
        url = this.getAttributeFromNotification(
          feedItem,
          ObjectType.object,
          "url"
        )
        break
      case "replied_in_thread":
        phrase = msg(str`${actor} replied in a thread.`)
        url = this.getAttributeFromNotification(
          feedItem,
          ObjectType.object,
          "url"
        )
        break
      case "published_chapter":
        phrase = msg(str`${actor} published chapter ${object}.`)
        url = this.getAttributeFromNotification(
          feedItem,
          ObjectType.object,
          "url"
        )
        break
      case "shared_script":
        phrase = msg(str`${actor} shared script ${object}.`)
        url = this.getAttributeFromNotification(
          feedItem,
          ObjectType.object,
          "url"
        )
        break
    }
    return html`
      <li>
        <a href="${url}">
          <div class="title">${phrase}</div>
          <div class="datetime">
            ${feedItem.created_at ? new Date(feedItem.created_at).toLocaleString("de-DE", {
              timeZone: "Europe/Berlin",

              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "numeric",
              minute: "numeric"
            }) : undefined}
          </div
          >
        </a>
      </li>`
  }

  /**
   * Get attribute of object (actor, object, target) in feedItem. Fetched feedItems have
   * a field `activities` which contains exactly one activity. feedItems from the websocket
   * don't have such a field.
   * This function can be extended to aggregate multiple objects in a grouped activity,
   * e.g. "Bob, Alice and two others"; currently grouping is turned off.
   * See: https://getstream.io/activity-feeds/docs/python/creating_feeds/?language=python
   *
   * @param feedItem feedItem to extract the object's attribute
   * @param objectType Type of object (actor, object, target)
   * @param attribute Name of attribute to be used, usually `name`
   */
  private getAttributeFromNotification(
    feedItem,
    objectType: ObjectType,
    attribute: string
  ) {
    // Check if feedItem is a grouped item; if items are grouped we have to decide how to
    // render multiple instances of activities into a string
    if (feedItem.activities) {
      try {
        return feedItem.activities[0][objectType]["data"][attribute]
      } catch {
        return null
      }
    }
    // If it's not grouped, simply get the value for the objectType
    else {
      try {
        return feedItem[objectType]["data"][attribute]
      } catch {
        return null
      }
    }
  }

  /**
   * Show dropdown and set unreadCount to zero; triggered by notification-icon
   */
  async toggleDropdown() {
    this._showDropdown = !this._showDropdown
    if (this._showDropdown) {
      document.addEventListener("click", this.handleClickWhenOpen.bind(this))
    }
    if (this.unreadCount > 0) {
      const response: FeedAPIResponse = await this._feed.get({
        mark_seen: true
      })
      this.unreadCount = response.unseen
    }
  }

  /**
   * Listener function to close the dropdown if user clicks outside of it
   * @param event ClickEvent anywhere on the page
   */
  private handleClickWhenOpen(event: MouseEvent) {
    if (!(event.target as HTMLElement).closest("notification-menu")) {
      this._showDropdown = false
      document.removeEventListener("click", this.handleClickWhenOpen)
    }
  }
}
